import type {CourseProgress} from '../../Hooks/LmsCourseApi/types';

type PageLike = {id: number; is_completed?: boolean};
type ChapterLike = {pages?: PageLike[]};

/**
 * Where the player opens, and what is already ticked: the first chapter
 * holding a page this student has not finished. Worked out from the pages
 * rather than the payload's `resume`, so a cached course that has been
 * marked up since (`withPageCompleted`) still opens in the right place. A
 * finished course, or one from an API that sends no progress, opens at the
 * start.
 */
export function initialPlace(
  course?: {chapters?: ChapterLike[]} | null,
): {index: number; completed: Set<number>} {
  const chapters = course?.chapters ?? [];
  const completed = new Set<number>();
  for (const chapter of chapters) {
    for (const page of chapter.pages ?? []) {
      if (page.is_completed) completed.add(page.id);
    }
  }
  if (!completed.size) return {index: 0, completed};
  const index = chapters.findIndex(chapter =>
    (chapter.pages ?? []).some(page => !completed.has(page.id)),
  );
  return {index: index < 0 ? 0 : index, completed};
}

/**
 * The cached course with one page marked finished, as a new object — so a
 * student who leaves and comes back inside the cache window resumes from
 * where they really are. The same object when the page is not in it.
 */
export function withPageCompleted<
  T extends {chapters: Array<{pages: PageLike[]}>},
>(course: T | undefined, pageId: number): T | undefined {
  if (!course) return course;
  const has = course.chapters.some(c => c.pages.some(p => p.id === pageId));
  if (!has) return course;
  return {
    ...course,
    chapters: course.chapters.map(c =>
      c.pages.some(p => p.id === pageId)
        ? {...c, pages: c.pages.map(p => (p.id === pageId ? {...p, is_completed: true} : p))}
        : c,
    ),
  };
}

/** What a course card offers, given how far the student got. */
export function progressCta(progress?: CourseProgress | null): {label: string; percent: number} {
  const percent = progress?.percent ?? 0;
  if (!progress || !progress.completed) return {label: 'Start course', percent: 0};
  if (progress.total > 0 && progress.completed >= progress.total) {
    return {label: 'Completed · Watch again', percent: 100};
  }
  return {label: `Continue · ${percent}% done`, percent};
}

/**
 * The chip on a finished course's cover. Admins see every course, so a draft
 * must not look like what students see.
 */
export function publicationChip(reviewState?: string | null): string {
  if (reviewState === 'draft') return 'DRAFT';
  if (reviewState === 'in_review') return 'IN REVIEW';
  return 'MASTERCLASS';
}
